import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect } from "react";
import { Form } from "react-bootstrap";
import z from "zod";
import type { Definition } from "@/briefpoint-client";
import FormFieldError from "@/components/FormFieldError";
import { Modal } from "@/components/Modals/Modal";
import { toast } from "@/components/ui/toast";
import { useDefinitionsApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { getDefinitionsOptions } from "@/lib/query-options";
import { queryClient } from "@/lib/queryClient";
import { getFieldError, normalizeError } from "@/lib/utils";

interface AddEditDefinitionProps {
  show: boolean;
  definition?: Definition;
  onClose: () => void;
  onConfirm: (value: Definition) => void;
  allDefinitions: Definition[];
}
export default function AddEditDefinition(props: AddEditDefinitionProps) {
  /* check if term is empty or already exists in library */
  const formSchema = z.object({
    term: z
      .string()
      .trim()
      .min(1, "Term is required")
      .refine((term) => {
        return (
          props.definition ||
          !props.allDefinitions.some(
            (d) => d.term?.toLowerCase() === term.toLowerCase(),
          )
        );
      }, "Term already exists in library"),
    aliases: z.string(),
    content: z.string().trim().min(1, "Content is required"),
    isPersonal: z.boolean(),
  });

  const { user } = useAuth();
  const definitionApi = useDefinitionsApi();

  const form = useForm({
    defaultValues: {
      term: props.definition?.term ?? "",
      aliases: props.definition?.aliases ?? "",
      content: props.definition?.content ?? "",
      isPersonal: props.definition?.firmStandardId !== user?.firmId,
    },
    validators: {
      onChange: formSchema,
    },
    onSubmit: async ({ value }) => {
      if (props.definition) {
        updateDefinitionMutation.mutate({
          ...value,
          id: props.definition.id,
          userId: user?.id,
          isDeleted: false,
        });
      } else {
        createDefinitionMutation.mutate(value);
      }
    },
  });

  const createDefinitionMutation = useMutation({
    mutationFn: (definition: Definition) => {
      return definitionApi.definitionCreate({
        definition: {
          ...definition,
          firmStandardId: form.state.values.isPersonal
            ? undefined
            : user?.firmId,
          id: undefined,
          isInternal: true,
          content: definition.content ?? "",
        },
      });
    },
    onSuccess: () => {
      toast.success("Definition created successfully");
    },
    onError: (error) => {
      toast.error("Failed to create definition. Please try again later.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getDefinitionsOptions(definitionApi),
      });
      props.onClose();
    },
  });

  const updateDefinitionMutation = useMutation({
    mutationFn: (definition: Definition) => {
      return definitionApi.definitionUpdate({
        definition: {
          ...definition,
          firmStandardId: form.state.values.isPersonal
            ? undefined
            : (user?.firmId ?? ""),
        },
      });
    },
    onSuccess: () => {
      toast.success("Definition updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update definition. Please try again later.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getDefinitionsOptions(definitionApi),
      });
      props.onClose();
    },
  });

  /* biome-ignore lint/correctness/useExhaustiveDependencies: need to reset when showing modal */
  useEffect(() => {
    if (props.show) {
      form.reset();
    }
  }, [props.show]);

  return (
    <Modal
      title={props.definition ? "Edit Term" : "Add a Term"}
      cancelText="Cancel"
      confirmText="Save"
      show={props.show}
      onClose={props.onClose}
      onConfirm={form.handleSubmit}
      confirming={
        createDefinitionMutation.isPending || updateDefinitionMutation.isPending
      }
      disableConfirmButton={
        createDefinitionMutation.isPending ||
        updateDefinitionMutation.isPending ||
        !form.state.isValid
      }
    >
      <div className="mb-2">
        <label htmlFor="term">Term</label>
        <form.Field
          name="term"
          children={(field) => (
            <>
              <Form.Control
                type="text"
                disabled={!!props.definition} // can not edit term if already exists in library
                id="term"
                size="sm"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              <FormFieldError errorMessage={getFieldError(field)} />
            </>
          )}
        />
      </div>
      <div className="mb-2">
        <label htmlFor="aliases">Aliases (Optional)</label>
        <form.Field
          name="aliases"
          children={(field) => (
            <>
              <Form.Control
                size="sm"
                type="text"
                id="aliases"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              <FormFieldError errorMessage={getFieldError(field)} />
            </>
          )}
        />
      </div>
      <div className="mb-2">
        <label htmlFor="content">Content</label>
        <form.Field
          name="content"
          children={(field) => (
            <>
              <Form.Control
                size="sm"
                as="textarea"
                id="content"
                value={field.state.value}
                rows={4}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              <FormFieldError errorMessage={getFieldError(field)} />
            </>
          )}
        />
      </div>
      <form.Field
        name="isPersonal"
        children={(field) => (
          <Form.Group id="new-firm-select">
            <Form.Check
              id="new-firm-select-personal"
              type="radio"
              inline
              label="Personal Library"
              checked={field.state.value === true}
              onChange={() => field.handleChange(true)}
            />
            <Form.Check
              id="new-firm-select-firm"
              type="radio"
              inline
              label="Firm Library"
              checked={field.state.value === false}
              onChange={() => field.handleChange(false)}
            />
          </Form.Group>
        )}
      />
    </Modal>
  );
}
